import React, { useState } from 'react';
import { loadOrCreateIdentity } from '../lib/identity';
import { getProfile, saveProfile, UserProfile } from '../lib/metadata';
import { publishEvent } from '../lib/sync';
import { seedFile } from '../lib/torrent';
import { sanitizeCSSProperties } from '../lib/sanitize';
import { COLORS, SPACING } from '../styles/theme';

interface ProfileEditorProps {
  onDone: () => void;
}

const MAX_PROFILE_BYTES = 8 * 1024;

const ACCENT_PRESETS = ['#00ff88', '#ff6b00', '#4488ff', '#ff0066', '#ffdd00', '#cc44ff', '#00ccff', '#ff4444', '#aaffaa', '#ff8800', '#44ffcc', '#ffffff'];

export function ProfileEditor({ onDone }: ProfileEditorProps) {
  const identity = loadOrCreateIdentity();
  const existing: UserProfile = getProfile() || {
    userId: identity.userId,
    publicKey: identity.publicKey,
    createdAt: identity.createdAt,
  };

  const [username, setUsername] = useState(existing.username || '');
  const [bio, setBio] = useState(existing.bio || '');
  const [links, setLinks] = useState<Array<{ label: string; url: string }>>(existing.links || []);
  const [accentColor, setAccentColor] = useState(existing.accentColor || COLORS.accent);
  const [avatarShape, setAvatarShape] = useState<'square' | 'circle'>(existing.avatarShape || 'square');
  const [bioFont, setBioFont] = useState<'monospace' | 'sans-serif'>(existing.bioFont || 'monospace');
  const [cssRaw, setCssRaw] = useState(
    Object.entries(existing.cssProperties || {}).map(([k, v]) => `${k}: ${v}`).join('\n')
  );
  const [socialGraphPublic, setSocialGraphPublic] = useState(existing.socialGraphPublic || false);
  const [displayPreference, setDisplayPreference] = useState<'username' | 'id'>(existing.displayPreference || 'id');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const { magnetURI } = await seedFile(file);
    saveProfile({ ...buildProfile(), avatarMagnetURI: magnetURI });
  }

  function parseCSSInput(raw: string): Record<string, string> {
    const result: Record<string, string> = {};
    raw.split('\n').forEach(line => {
      const idx = line.indexOf(':');
      if (idx === -1) return;
      const key = line.slice(0, idx).trim();
      const value = line.slice(idx + 1).trim();
      if (key && value) result[key] = value;
    });
    return sanitizeCSSProperties(result);
  }

  function buildProfile(): UserProfile {
    return {
      ...existing,
      username: username.trim() || undefined,
      bio: bio.trim() || undefined,
      links: links.filter(l => l.url.trim()),
      accentColor,
      avatarShape,
      bioFont,
      cssProperties: parseCSSInput(cssRaw),
      socialGraphPublic,
      displayPreference,
    };
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    const profile = buildProfile();
    const bytes = new TextEncoder().encode(JSON.stringify(profile)).length;
    if (bytes > MAX_PROFILE_BYTES) {
      setError(`Profile too large (${bytes} bytes, limit 8192). Shorten bio or reduce CSS properties.`);
      setSaving(false);
      return;
    }
    saveProfile(profile);
    await publishEvent('profile', profile);
    setSaving(false);
    onDone();
  }

  function addLink() {
    if (links.length >= 5) return;
    setLinks([...links, { label: '', url: '' }]);
  }

  function updateLink(i: number, field: 'label' | 'url', value: string) {
    setLinks(links.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  }

  function removeLink(i: number) {
    setLinks(links.filter((_, idx) => idx !== i));
  }

  const fieldStyle = {
    width: '100%', fontFamily: 'monospace', fontSize: '13px',
    background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`,
    padding: SPACING.xs, boxSizing: 'border-box' as const,
  };

  return (
    <div style={{ padding: SPACING.lg, maxWidth: 480, fontFamily: 'monospace' }}>
      <h2 style={{ color: COLORS.text, marginBottom: SPACING.lg, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: SPACING.sm, fontSize: '16px' }}>
        Edit Profile
      </h2>

      <label style={{ display: 'block', fontSize: '12px', color: COLORS.textMuted, marginBottom: 4 }}>Display name</label>
      <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="Choose a name (not unique)"
        style={{ ...fieldStyle, marginBottom: SPACING.md }} />

      <label style={{ display: 'block', fontSize: '12px', color: COLORS.textMuted, marginBottom: 4 }}>Bio</label>
      <textarea value={bio} onChange={e => setBio(e.target.value)} rows={3}
        style={{ ...fieldStyle, resize: 'vertical', marginBottom: SPACING.md }} />

      <label style={{ display: 'block', fontSize: '12px', color: COLORS.textMuted, marginBottom: 4 }}>Links (up to 5)</label>
      {links.map((link, i) => (
        <div key={i} style={{ display: 'flex', gap: SPACING.sm, marginBottom: 4 }}>
          <input placeholder="Label" value={link.label} onChange={e => updateLink(i, 'label', e.target.value)}
            style={{ width: 100, fontFamily: 'monospace', fontSize: '12px', background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, padding: '2px 4px' }} />
          <input placeholder="https://..." value={link.url} onChange={e => updateLink(i, 'url', e.target.value)}
            style={{ flex: 1, fontFamily: 'monospace', fontSize: '12px', background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, padding: '2px 4px' }} />
          <button onClick={() => removeLink(i)} style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}>×</button>
        </div>
      ))}
      {links.length < 5 && (
        <button onClick={addLink} style={{ background: 'transparent', border: `1px dashed ${COLORS.border}`, color: COLORS.textMuted, padding: `2px ${SPACING.sm}`, cursor: 'pointer', fontSize: '12px', width: '100%', marginBottom: SPACING.md }}>
          + Add link
        </button>
      )}

      <label style={{ display: 'block', fontSize: '12px', color: COLORS.textMuted, marginBottom: 4 }}>Avatar (seeded via P2P)</label>
      <input type="file" accept="image/*" onChange={handleAvatarUpload}
        style={{ fontFamily: 'monospace', fontSize: '12px', color: COLORS.text, marginBottom: SPACING.md }} />

      <h3 style={{ color: COLORS.text, fontSize: '13px', borderTop: `1px solid ${COLORS.border}`, paddingTop: SPACING.md, marginBottom: SPACING.md }}>Appearance</h3>

      <label style={{ display: 'block', fontSize: '12px', color: COLORS.textMuted, marginBottom: 4 }}>Accent color</label>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: SPACING.md, flexWrap: 'wrap' }}>
        {ACCENT_PRESETS.map(c => (
          <div key={c} onClick={() => setAccentColor(c)}
            style={{ width: 20, height: 20, background: c, cursor: 'pointer', border: accentColor === c ? `3px solid ${COLORS.text}` : `1px solid ${COLORS.border}` }} />
        ))}
        <input type="text" value={accentColor} onChange={e => setAccentColor(e.target.value)} placeholder="#rrggbb"
          style={{ width: 80, fontFamily: 'monospace', fontSize: '11px', background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, padding: '2px 4px' }} />
      </div>

      <div style={{ display: 'flex', gap: SPACING.lg, marginBottom: SPACING.sm }}>
        {(['square', 'circle'] as const).map(s => (
          <label key={s} style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, fontSize: '12px', color: COLORS.text, cursor: 'pointer' }}>
            <input type="radio" name="avatarShape" checked={avatarShape === s} onChange={() => setAvatarShape(s)} />
            {s.charAt(0).toUpperCase() + s.slice(1)} avatar
          </label>
        ))}
      </div>

      <div style={{ display: 'flex', gap: SPACING.lg, marginBottom: SPACING.md }}>
        {(['monospace', 'sans-serif'] as const).map(f => (
          <label key={f} style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, fontSize: '12px', color: COLORS.text, cursor: 'pointer' }}>
            <input type="radio" name="bioFont" checked={bioFont === f} onChange={() => setBioFont(f)} />
            {f === 'monospace' ? 'Monospace' : 'Sans-serif'} bio
          </label>
        ))}
      </div>

      <h3 style={{ color: COLORS.text, fontSize: '13px', borderTop: `1px solid ${COLORS.border}`, paddingTop: SPACING.md, marginBottom: SPACING.sm }}>
        Advanced — CSS Custom Properties
      </h3>
      <p style={{ fontSize: '11px', color: COLORS.textMuted, marginBottom: SPACING.sm, lineHeight: 1.5 }}>
        One per line: <code>--color-accent: #ff0088</code>
      </p>
      <textarea value={cssRaw} onChange={e => setCssRaw(e.target.value)} rows={5}
        style={{ ...fieldStyle, resize: 'vertical', marginBottom: SPACING.md }} />

      <h3 style={{ color: COLORS.text, fontSize: '13px', borderTop: `1px solid ${COLORS.border}`, paddingTop: SPACING.md, marginBottom: SPACING.sm }}>Privacy</h3>
      <label style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, fontSize: '12px', color: COLORS.text, cursor: 'pointer', marginBottom: SPACING.md }}>
        <input type="checkbox" checked={socialGraphPublic} onChange={() => setSocialGraphPublic(!socialGraphPublic)} />
        Show follower / following counts publicly on my profile
      </label>

      <p style={{ fontSize: '12px', color: COLORS.text, marginBottom: SPACING.xs }}>How should your name appear on posts?</p>
      <label style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, fontSize: '12px', color: COLORS.text, cursor: 'pointer', marginBottom: SPACING.xs }}>
        <input type="radio" name="displayPreference" checked={displayPreference === 'username'} onChange={() => setDisplayPreference('username')} />
        My username{username.trim() ? ` (${username.trim()})` : ' (set a username above first)'}
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, fontSize: '12px', color: COLORS.text, cursor: 'pointer', marginBottom: SPACING.md }}>
        <input type="radio" name="displayPreference" checked={displayPreference !== 'username'} onChange={() => setDisplayPreference('id')} />
        My scrambled ID (pseudonymous)
      </label>

      {error && <p style={{ color: COLORS.danger, fontSize: '12px', marginBottom: SPACING.sm }}>{error}</p>}

      <div style={{ display: 'flex', gap: SPACING.sm }}>
        <button onClick={handleSave} disabled={saving}
          style={{ background: COLORS.accent, color: '#fff', border: 'none', padding: `${SPACING.sm} ${SPACING.lg}`, cursor: saving ? 'default' : 'pointer', fontFamily: 'monospace', fontSize: '13px', fontWeight: 'bold' }}>
          {saving ? 'Saving...' : 'Save profile'}
        </button>
        <button onClick={onDone}
          style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, padding: `${SPACING.sm} ${SPACING.lg}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '13px' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
